// this mutator is needed for deleteConversationItemParts
import { isItemBodyHeldByReadingPane } from "./tryRemoveFromMailStoreItemsMutator";

import loadInitialMailFolderState from "./loadInitialMailFolderState";
import { initializeAlwaysInCacheTableViews } from "owa-mail-triage-local-updates/lib/actions/tableCache/alwaysInCacheTableViews";
import { initializeMailLayout } from "owa-mail-layout";
import mailStore from "owa-mail-store/lib/store/Store";
import getMessageCache from "owa-mail-store/lib/store/messageCache";
import { lazyReportConversationCacheEviction, lazyReportMessageCacheEviction, } from "owa-mail-body-compression";
import getConversationCache from "owa-mail-store/lib/store/conversationCache";
import deleteConversationItemParts from "owa-mail-store-actions/lib/actions/deleteConversationItemParts";
import tryRemoveFromMailStoreItems, { type RemoveItemSource } from "owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems";
import { initializeOptionValues } from "owa-outlook-service-option-store";
import { lazyInitializeWin32StyleComposeFormOption } from "owa-compose-popout-option/lib/lazyFunctions";
import { getPrimeBootSettingsOptionsValues } from "owa-session-store";
import { loadCommandBarActions } from "owa-mail-commandbar-actions";
import loadMailRibbonConfiguration from "owa-mail-ribbon-store/lib/util/loadMailRibbonConfiguration";
import { initializeCommandingViewMode } from "owa-command-ribbon-store";
import type { SessionData } from "owa-service/lib/types/SessionData";
import { initializeSuiteHeaderPinnedPane } from "owa-suite-header-auto-open";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { initializeRestoreMailListener } from "owa-freeze-dry-mail/lib/utils/handleRestoreScrollPosition";
import { initializePersistMailListener } from "owa-freeze-dry-mail/lib/utils/handlePersistScrollPosition";
import { updateServiceConfig } from "owa-service/lib/config";
import { getAudienceForCallsToOWS } from "owa-service-request-config";
import { initializeOcpsPolicyStore } from "owa-ocps-get-policies";
import { lazyInitializeOcpsPoliciesFromOwsPrime } from "owa-ocps-policies-fetch";
import { initializeCopilotCommandCenter } from "owa-copilot-command-center/lib/utils/initializeCopilotCommandCenter";
import { loadQuickSteps } from "owa-mail-quick-steps-store/lib/util/loadQuickSteps";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import initializeShowPreviewLinesInListViewSetting from "owa-messagelist-layout-option/lib/utils/initializeShowPreviewLinesInListViewSetting";
import { initializeClarity } from "owa-clarity";
import initializePersonaCheckboxDisplaySetting from "owa-messagelist-layout-option/lib/utils/initializePersonaCheckboxDisplaySetting";
import { isWin32StylingEnabled } from "owa-theme-win32-style-store";
import { ensureWin32IconsRegistered } from "owa-command-ribbon-win32-icon";
import { isSingleLineRibbon } from "owa-command-ribbon-store/lib/selectors/isSingleLineRibbon";
import { filterToMailboxInfoForAccountSources, getAllMailAccountSources, accountSourceDataTypeChecker, } from "owa-account-source-list-store";
import { lazyInitializeSenderFollowUpProcessing } from "owa-mail-store/lib/lazyFunctions";
import { trace } from "owa-trace";
import { isCapabilityEnabled, isCapabilitySupported } from "owa-capabilities";
import { advancedFlagCapability } from "owa-capabilities-definitions/lib/advancedFlagCapability";
import { serviceRequestCapability } from "owa-capabilities-definitions/lib/serviceRequestCapability";
import { lazyTryEvictListOnlyMessageBody } from "owa-mail-store-actions";

export default function initializeMailModuleState(sessionData?: SessionData | undefined): void {
    const isBodyEvictionEnabled = isGlobalFeatureEnabled("mon-mail-body-eviction");
    const isItemBodyCompressionEnabled = isGlobalFeatureEnabled("mon-mail-item-body-compression") && !isBodyEvictionEnabled;
    
    // Passing a delegate here as the delete callback to allow deleteConversationItemParts to be fully loaded by webpack.
    getConversationCache().initialize(mailStore().conversations, key => {
        if (isGlobalFeatureEnabled("mon-mail-cold-body-compression") && !isBodyEvictionEnabled) {
            void lazyReportConversationCacheEviction.importAndExecute(key).then(() => deleteConversationItemParts(key), () => deleteConversationItemParts(key));
        }
        else {
            deleteConversationItemParts(key);
        }
    });
    if (isItemBodyCompressionEnabled || isBodyEvictionEnabled) {
        getMessageCache().initialize(mailStore().items, key => {
            if (isBodyEvictionEnabled) {
                const expectedItem = mailStore().items.get(key);
                // tryRemoveFromMailStoreItems will attempt to remove the item from the mail store before eviction.
                tryRemoveFromMailStoreItems(key, 3);
                
                // Attempt to evict the message body if it still exists in the mail store.
                if (expectedItem && mailStore().items.get(key) === expectedItem) {
                    // Eviction is best-effort; ownership is rechecked after the lazy bundle loads.
                    lazyTryEvictListOnlyMessageBody
                        .import()
                        .then(tryEvictListOnlyMessageBody => tryEvictListOnlyMessageBody(key, expectedItem, isItemBodyHeldByReadingPane))
                        .catch(() => { });
                }
            }
            else {
                void lazyReportMessageCacheEviction.importAndExecute(key).then(() => tryRemoveFromMailStoreItems(key, 3), () => tryRemoveFromMailStoreItems(key, 3));
            }
        });
    }
    
    initializePersistMailListener();
    initializeRestoreMailListener();
    
    // Initialize Clarity as early as possible in module state setup.
    void initializeClarity();
    
    const mailboxInfo = getModuleContextMailboxInfo();
    const options = getPrimeBootSettingsOptionsValues(mailboxInfo);
    
    initializeOptionValues(options);
    
    initializeCommandingViewMode(options, mailboxInfo);
    
    if (isWin32StylingEnabled()) {
        if (isGlobalFeatureEnabled("mon-win32-styling-force-compose-popout")) {
            lazyInitializeWin32StyleComposeFormOption.importAndExecute().catch(() => {
                trace.warn("Failed to initialize the Win32 style compose pop-out preference.");
            });
        }
        void ensureWin32IconsRegistered(!isSingleLineRibbon());
    }
    
    initializeSuiteHeaderPinnedPane(options);
    
    loadCommandBarActions();
    
    initializeAlwaysInCacheTableViews(mailboxInfo);
    
    loadInitialMailFolderState(mailboxInfo, sessionData);
    
    initializeMailLayout();
    
    void initializeShowPreviewLinesInListViewSetting();
    
    if (isGlobalFeatureEnabled("tri-mlCheckboxBehavior")) {
        void initializePersonaCheckboxDisplaySetting();
    }
    
    initializeOcpsPolicyStore("initializeMailModuleState");
    lazyInitializeOcpsPoliciesFromOwsPrime.importAndExecute("initializeMailModuleState");
    
    loadMailRibbonConfiguration();
    
    initializeCopilotCommandCenter(mailboxInfo);
    
    updateServiceConfig({ getAudienceForCallsToOWS });
    
    loadQuickSteps();
    
    const followUpSources = getAllMailAccountSources().flatMap(account => accountSourceDataTypeChecker.isM365UserMailbox(account)
        ? [account, ...account.sharedSources.sources]
        : [account]);
    for (const accountMailboxInfo of filterToMailboxInfoForAccountSources(followUpSources)) {
        if ((accountMailboxInfo.type !== "UserMailbox" &&
            accountMailboxInfo.type !== "SharedMailbox") ||
            !isGlobalFeatureEnabled("cmp-sender-follow-up") ||
            !isCapabilityEnabled(advancedFlagCapability, accountMailboxInfo) ||
            !isCapabilitySupported(serviceRequestCapability, accountMailboxInfo)) {
            continue;
        }
        void lazyInitializeSenderFollowUpProcessing
            .import()
            .then(initializeProcessing => initializeProcessing(accountMailboxInfo))
            .catch(error => {
            const reason = error instanceof Error ? error.message : String(error);
            trace.warn(`ComposeFollowUpRecoveryFailed: ${reason}`);
        });
    }
}
